import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, History, Printer, RefreshCw, Search, Tag } from 'lucide-react';
import { api } from '@/lib/api';
import { imprimirEtiquetas } from '@/lib/imprimirEtiquetas';
import { cn, numero } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { EtiquetaPapel, type EtiquetaSeparacao } from './EtiquetaPapel';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { Esqueleto, Pilula } from '@/componentes/ui/primitivos';
import { useSessao } from '@/hooks/useSessao';
import { Segmentado } from '@/componentes/ui/segmentado';

/**
 * As etiquetas da onda.
 *
 * Duas origens, e a primeira é a que importa no dia a dia:
 *
 *   - **Onda montada** — as etiquetas dos pedidos que VOCÊ marcou, calculadas
 *     como a procedure do ERP calcularia: uma por unidade, posição na ordem do
 *     PBS, disponível consumido ao longo da onda inteira. É prévia: sai sem
 *     código de barras, porque o código deriva do id que só nasce na emissão.
 *   - **Emitidas** — as ondas já gravadas (aqui ou no Procfit), para
 *     reimprimir. Essas têm código de barras de verdade.
 *
 * Antes esta aba mostrava só a segunda lista, o que estava errado de conceito:
 * quem monta uma onda com um pedido quer ver a etiqueta DAQUELE pedido, não o
 * histórico do CD.
 */

interface Onda {
  emissao: number;
  dataHora: string | null;
  usuario: number | null;
  usuarioNome: string | null;
  empresa: number | null;
  pedidos: number;
  etiquetas: number;
  centros: number[];
}

function dataHoraBr(iso: string | null): string {
  if (!iso) return '—';
  const [data = '', hora = ''] = iso.split(' ');
  const [a, m, d] = data.split('-');
  const dia = d && m && a ? `${d}/${m}/${a}` : data;
  return hora ? `${dia} ${hora.slice(0, 5)}` : dia;
}

function agora(): string {
  return new Date().toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function Etiquetas({
  empresa,
  daOnda,
  pedidosMarcados,
  emissaoInicial = null,
}: {
  empresa: number;
  /** As etiquetas da seleção atual, já calculadas. */
  daOnda: readonly EtiquetaSeparacao[];
  pedidosMarcados: number;
  /** Onda recém-emitida: a aba abre nela, com código de barras de verdade. */
  emissaoInicial?: number | null;
}): JSX.Element {
  const { usuario } = useSessao();
  /*
   * Abre SEMPRE na onda montada — mesmo sem nada marcado.
   *
   * Antes, sem seleção, a aba caía para "Emitidas" e ainda escolhia a onda
   * mais recente sozinha: quem abria a tela via etiqueta de um pedido alheio,
   * emitido por outra pessoa, sem ter pedido nada. Histórico é consulta, e
   * consulta não se oferece de graça no lugar do trabalho do dia — vira
   * confusão e, no pior caso, papel errado impresso.
   *
   * Sem seleção a aba fica vazia e diz o que fazer. "Emitidas" é um clique.
   */
  const [origem, definirOrigem] = React.useState<'onda' | 'emitidas'>(
    emissaoInicial !== null ? 'emitidas' : 'onda',
  );
  const [emissao, definirEmissao] = React.useState<number | null>(emissaoInicial);
  const folhaRef = React.useRef<HTMLDivElement | null>(null);
  /** Número digitado à mão — para abrir onda que a lista de 15 dias não alcança. */
  const [numeroDigitado, definirNumeroDigitado] = React.useState('');
  /** Reimprimir só um pedido da onda, em vez da onda inteira. */
  const [soPedido, definirSoPedido] = React.useState<number | null>(null);
  /** Filtro do pedido dentro da onda — só entra em cena quando são muitos. */
  const [buscaPedido, definirBuscaPedido] = React.useState('');
  const [impressoEm, definirImpressoEm] = React.useState(agora);

  const ondas = useQuery<Onda[]>({
    queryKey: ['separacao', 'ondas', empresa],
    queryFn: () => api.get<Onda[]>('/api/separacao/ondas', { empresa, dias: 15 }),
    enabled: origem === 'emitidas',
    staleTime: 60_000,
  });

  const lista = useQuery<{ emissao: number; etiquetas: EtiquetaSeparacao[] }>({
    queryKey: ['separacao', 'etiquetas', emissao],
    queryFn: () =>
      api.get<{ emissao: number; etiquetas: EtiquetaSeparacao[] }>('/api/separacao/etiquetas', {
        emissao,
      }),
    enabled: origem === 'emitidas' && emissao !== null,
    staleTime: 60_000,
  });

  // Marcar mais um pedido tem de trazer a aba de volta para a onda.
  React.useEffect(() => {
    if (daOnda.length > 0) definirOrigem('onda');
  }, [daOnda.length]);

  /*
   * Acabou de emitir: a aba passa para a onda NOVA.
   *
   * É a continuidade do gesto — quem emitiu quer a etiqueta com código de
   * barras, agora, e não a prévia de uma seleção que já foi embora.
   */
  React.useEffect(() => {
    if (emissaoInicial !== null) {
      definirEmissao(emissaoInicial);
      definirOrigem('emitidas');
    }
  }, [emissaoInicial]);

  /*
   * "Emitidas" abre VAZIA, de propósito.
   *
   * Antes ela escolhia sozinha a onda mais recente. Parecia prestativo e
   * enganava: quem chega aqui procurando UM pedido via um maço de etiquetas de
   * outra onda já na tela, sem ter pedido nada, e não havia como distinguir
   * "estas são as suas" de "estas são as últimas que saíram". Imprimir o maço
   * errado custa bobina e confunde a separação.
   *
   * A lista das últimas ondas continua ali para escolher com um clique — o que
   * mudou é que a escolha passou a ser do usuário.
   */

  React.useEffect(() => {
    definirSoPedido(null);
    definirBuscaPedido('');
  }, [emissao, origem]);

  const naOnda = origem === 'onda';
  const todas = naOnda ? daOnda : (lista.data?.etiquetas ?? []);

  /**
   * Os pedidos desta onda, com quantas etiquetas cada um tem.
   *
   * Sai das próprias etiquetas: uma onda é um número que agrupa VÁRIOS
   * pedidos, e reimprimir raramente é "a onda toda de novo" — é "aquele
   * pedido cuja etiqueta rasgou".
   */
  const pedidosDaOnda = React.useMemo(() => {
    const conta = new Map<number, number>();
    for (const e of todas) conta.set(e.televenda, (conta.get(e.televenda) ?? 0) + 1);
    return [...conta.entries()]
      .map(([televenda, etiquetas]) => ({ televenda, etiquetas }))
      .sort((a, b) => a.televenda - b.televenda);
  }, [todas]);

  /**
   * Quantos pedidos cabem no olho antes de virar parede.
   *
   * Doze é o ponto em que a lista ainda se varre de relance; onda de 53 (que
   * existe — a 268043 tem exatamente isso) passava de meia tela em botões.
   */
  const muitosPedidos = pedidosDaOnda.length > 12;
  const filtrados = React.useMemo(() => {
    if (!muitosPedidos) return pedidosDaOnda;
    if (buscaPedido === '') return [];
    return pedidosDaOnda.filter((p) => String(p.televenda).includes(buscaPedido));
  }, [pedidosDaOnda, muitosPedidos, buscaPedido]);
  const pedidosVisiveis = filtrados.slice(0, 10);
  const ocultos = filtrados.length - pedidosVisiveis.length;

  const etiquetas = soPedido === null ? todas : todas.filter((e) => e.televenda === soPedido);
  const ondaAtual = ondas.data?.find((o) => o.emissao === emissao) ?? null;

  const imprimir = (): void => {
    definirImpressoEm(agora());
    // O carimbo precisa estar no DOM antes de o iframe clonar o conteúdo.
    window.requestAnimationFrame(() => {
      const folha = folhaRef.current;
      if (folha) imprimirEtiquetas(folha);
    });
  };

  return (
    <div className="relative flex min-h-0 flex-1 flex-col gap-3">
      {/*
        Esta aba fala com o ERP e, nas emitidas, busca a onda inteira. Dois ou
        três segundos sem sinal parecem travamento, e a reação e clicar de novo
        — numa tela que imprime, isso vira papel jogado fora.
      */}
      <div className="cartao nao-imprimir flex flex-wrap items-center gap-2 px-3 py-2">
        <Segmentado
          valor={origem}
          aoMudar={definirOrigem}
          tamanho="pequeno"
          rotuloAcessivel="Origem das etiquetas"
          opcoes={[
            {
              valor: 'onda',
              rotulo: 'Onda montada',
              icone: <ClipboardList className="h-4 w-4" />,
              contagem: daOnda.length,
            },
            { valor: 'emitidas', rotulo: 'Emitidas', icone: <History className="h-4 w-4" /> },
          ]}
        />

        {naOnda ? (
          <p className="text-[12.5px] text-textoFraco">
            {daOnda.length === 0 ? (
              'Marque pedidos na aba Pedidos para ver as etiquetas da onda.'
            ) : (
              <>
                <b className="tabular text-texto">{numero(daOnda.length)}</b> etiquetas de{' '}
                <b className="tabular text-texto">{numero(pedidosMarcados)}</b>{' '}
                {pedidosMarcados === 1 ? 'pedido' : 'pedidos'} — uma por unidade
                {usuario ? (
                  <>
                    {' · '}
                    {/* Quem vai assinar a emissão é quem está logado: o
                        `USUARIO_LOGADO` sai da sessão, não de um campo. */}
                    sairá em nome de <b className="text-texto">{usuario.nome}</b>
                  </>
                ) : null}
              </>
            )}
          </p>
        ) : (
          <span className="flex flex-wrap items-center gap-2">
            <select
              value={emissao ?? ''}
              onChange={(e) => definirEmissao(Number(e.target.value) || null)}
              aria-label="Onda emitida"
              className="min-h-[32px] rounded-lg border border-borda bg-superficie px-2 text-[12.5px]"
            >
              {(ondas.data ?? []).map((o) => (
                <option key={o.emissao} value={o.emissao}>
                  {o.emissao} · {dataHoraBr(o.dataHora)} · {o.etiquetas} etq
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => void ondas.refetch()}
              title="Atualizar a lista de ondas"
              className="rounded-lg p-1 text-textoFraco transition-colors hover:bg-superficie2 hover:text-texto"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', ondas.isFetching && 'animate-spin')} />
            </button>

            {/*
              Abrir onda pelo NÚMERO.
              A lista ao lado cobre 15 dias; quem tem o número na mão — anotado
              no papel, vindo do Procfit, de uma onda de mês passado — ficava
              sem caminho nenhum.
            */}
            <form
              className="flex items-center gap-1"
              onSubmit={(e) => {
                e.preventDefault();
                const n = Number(numeroDigitado.trim());
                if (Number.isInteger(n) && n > 0) definirEmissao(n);
              }}
            >
              <input
                value={numeroDigitado}
                onChange={(e) => definirNumeroDigitado(e.target.value.replace(/\D/g, ''))}
                inputMode="numeric"
                placeholder="nº da onda"
                aria-label="Abrir onda pelo número"
                className="tabular min-h-[32px] w-[96px] rounded-lg border border-borda bg-superficie px-2 text-[12.5px]"
              />
              <Botao type="submit" variante="secundaria" tamanho="pequeno">
                Abrir
              </Botao>
            </form>
            {/*
              Este nome é quem emitiu ESTA onda — histórico do CD, quase sempre
              outra pessoa e quase sempre pelo Procfit. Sem o rótulo inteiro
              parece o usuário da sessão, e aí assusta com razão.
            */}
            {ondaAtual?.usuarioNome ? (
              <span
                className="max-w-[160px] truncate text-[11.5px] text-textoFraco"
                title={`Onda emitida por ${ondaAtual.usuarioNome}`}
              >
                por {ondaAtual.usuarioNome}
              </span>
            ) : null}
          </span>
        )}

        {/* Só a prévia ganha selo próprio: é aviso, não enfeite. O "10 × 5 cm"
            virou título do botão — dado bom de ter, mas não vale uma linha. */}
        {naOnda && daOnda.length > 0 ? (
          <Pilula tom="alerta" className="ml-auto">
            prévia · sem código de barras
          </Pilula>
        ) : null}
        {/*
          NA PRÉVIA NÃO SE IMPRIME.

          A onda ainda não foi emitida: a etiqueta não tem código de barras —
          ele deriva do id que só nasce na emissão. Imprimir aqui produz um maço
          de papel que o coletor não consegue bipar, e que é indistinguível do
          maço bom depois de sair da impressora. O botão sai de cena em vez de
          ficar desabilitado porque não há nada que o usuário possa fazer para
          habilitá-lo nesta aba — o caminho é emitir a onda.
        */}
        {naOnda && daOnda.length > 0 ? (
          <span className="text-[11.5px] text-textoFraco">
            emita a onda para imprimir com código de barras
          </span>
        ) : (
          <Botao
            variante="primaria"
            tamanho="pequeno"
            disabled={etiquetas.length === 0}
            className="ml-auto"
            title="Papel de 100 × 50 mm, uma etiqueta por página — igual ao RTM do Procfit. Para PDF, escolha 'Salvar como PDF' no destino."
            onClick={imprimir}
          >
            <Printer className="h-3.5 w-3.5" />
            Imprimir / PDF {etiquetas.length > 0 ? numero(etiquetas.length) : ''}
          </Botao>
        )}
      </div>

      {/*
        Os PEDIDOS da onda — para reimprimir só um deles.

        Reimprimir quase nunca é "a onda inteira de novo": é aquele pedido cuja
        etiqueta rasgou na esteira. Mas onda de 53 pedidos vira uma parede de
        botões que ocupa meia tela e não se lê — então a lista só aparece
        enquanto couber no olho (até 12). Passando disso, o gesto vira BUSCA:
        digita-se o número, que é o que se tem na mão quando a etiqueta rasgou.
      */}
      {!naOnda && pedidosDaOnda.length > 0 ? (
        <div className="cartao nao-imprimir flex flex-wrap items-center gap-1.5 px-3 py-2">
          <span className="rotulo mr-0.5">
            {pedidosDaOnda.length} {pedidosDaOnda.length === 1 ? 'pedido' : 'pedidos'}
          </span>

          <button
            type="button"
            onClick={() => {
              definirSoPedido(null);
              definirBuscaPedido('');
            }}
            className={cn(
              'rounded-lg px-2 py-1 text-[12px] font-semibold transition-colors',
              soPedido === null
                ? 'gradiente-marca text-white'
                : 'bg-superficie2 text-textoFraco hover:text-texto',
            )}
          >
            todos ({numero(todas.length)})
          </button>

          {muitosPedidos ? (
            <span className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-textoSuave" />
              <input
                value={buscaPedido}
                onChange={(e) => definirBuscaPedido(e.target.value.replace(/\D/g, ''))}
                inputMode="numeric"
                placeholder="filtrar pedido"
                aria-label="Filtrar pedido desta onda"
                className="tabular min-h-[30px] w-[130px] rounded-lg border border-borda bg-superficie pl-7 pr-2 text-[12px]"
              />
            </span>
          ) : null}

          {/* Com poucos pedidos, todos aparecem. Com muitos, só os que a busca
              alcança — e um teto de 10, porque busca que devolve 40 é parede
              de novo. */}
          {pedidosVisiveis.map((p) => {
            const ativo = soPedido === p.televenda;
            return (
              <button
                key={p.televenda}
                type="button"
                onClick={() => definirSoPedido(ativo ? null : p.televenda)}
                title={`Imprimir só as etiquetas do pedido ${p.televenda}`}
                className={cn(
                  'tabular rounded-lg px-2 py-1 font-mono text-[12px] transition-colors',
                  ativo
                    ? 'gradiente-marca text-white'
                    : 'bg-superficie2 text-texto hover:bg-superficie3',
                )}
              >
                {p.televenda}
                <span className={cn('ml-1', ativo ? 'text-white/70' : 'text-textoFraco')}>
                  ({p.etiquetas})
                </span>
              </button>
            );
          })}

          {muitosPedidos && buscaPedido === '' ? (
            <span className="text-[11.5px] text-textoFraco">
              digite o número para achar um pedido
            </span>
          ) : null}
          {muitosPedidos && buscaPedido !== '' && pedidosVisiveis.length === 0 ? (
            <span className="text-[11.5px] text-textoFraco">
              nenhum pedido com “{buscaPedido}” nesta onda
            </span>
          ) : null}
          {ocultos > 0 ? (
            <span className="text-[11.5px] text-textoFraco">e mais {ocultos}…</span>
          ) : null}
        </div>
      ) : null}
      {origem === 'emitidas' && lista.isLoading ? (
        <div className="space-y-2">
          <Esqueleto className="h-28" />
          <Esqueleto className="h-28" />
        </div>
      ) : origem === 'emitidas' && lista.isError ? (
        <EstadoErro erro={lista.error} aoTentarDeNovo={() => void lista.refetch()} />
      ) : etiquetas.length === 0 ? (
        <div className="cartao grid flex-1 place-items-center p-10">
          <EstadoVazio
            icone={<Tag className="h-7 w-7" />}
            titulo={naOnda ? 'Nenhum pedido marcado' : 'Esta onda não tem etiquetas'}
            detalhe={
              naOnda
                ? 'As etiquetas saem dos pedidos que você marca na aba Pedidos — uma por unidade.'
                : 'O cabeçalho existe, mas nenhuma linha foi gerada em ETIQUETAS_SEPARACAO.'
            }
          />
        </div>
      ) : (
        <div className="cartao min-h-0 flex-1 overflow-auto bg-superficie2 p-3">
          <div ref={folhaRef} className="grade-previa mx-auto grid w-fit gap-3">
            {etiquetas.map((e) => (
              <EtiquetaPapel
                key={e.etiqueta}
                etiqueta={e}
                impressoEm={impressoEm}
                previa
                naoEmitida={naOnda}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
